import React, { createContext, FC, useEffect } from 'react';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useLocation } from 'react-router-dom';
import useFetchState, { FetchState } from '../hooks/useFetchState';
import { getSorisOrderNumberBarcode } from '../api/returns/soris-api';
import { ISORISOrderNumberBarcodeResponse } from '../models/soris';
import { getEjKey } from '../utils/order-details/OrderDetailsUtils';
import { IOrderDetailsResponse } from '../models/order-details';
import { isCashNCarryOrder } from '../components/shared/order-number-formatter/OrderNumberFormatter';
import { getTransactionType } from '../utils/returns/returns-helper';
import { FeatureSwitches } from '../constants/feature-switches';

interface IPhase0BarcodeProvider {
  orderDetailsData?: IOrderDetailsResponse | undefined;
  orderDetailsLoaded?: boolean;
  children?: React.ReactNode;
}

export interface IPhase0BarcodeContext {
  orderBarcodeApiState?: FetchState<ISORISOrderNumberBarcodeResponse>;
}

const defaultState: IPhase0BarcodeContext = {
  orderBarcodeApiState: undefined,
};

// Create the context with a default value
export const Phase0BarcodeContext =
  createContext<IPhase0BarcodeContext>(defaultState);

export const Phase0BarcodeProvider: FC<IPhase0BarcodeProvider> = ({
  orderDetailsData,
  orderDetailsLoaded,
  children,
}) => {
  const { isB2CCustomer } = useThdCustomer();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const isReturnReceiptOrder =
    orderDetailsData?.statusDescription === 'Returned';
  const isGuestOrder = location.pathname === '/myaccount/guest-order-tracking';

  const orderNumber = urlParams.get('orderNumber')
    ? String(urlParams.get('orderNumber'))
    : orderDetailsData?.orderNumber;

  const transactionId = urlParams.get('transactionId')
    ? Number(urlParams.get('transactionId'))
    : orderDetailsData?.transactionId;

  const registerNumber = urlParams.get('registerNumber')
    ? Number(urlParams.get('registerNumber'))
    : orderDetailsData?.registerNumber;

  const transactionType = getTransactionType(
    urlParams.get('transactionType'),
    orderDetailsData,
  );

  // Initializing the order barcode API state hook
  const orderBarcodeApiState =
    useFetchState<ISORISOrderNumberBarcodeResponse>();
  const { data, isFetched } = orderBarcodeApiState;

  const isCashNCarryOrderNumber = isCashNCarryOrder(
    orderNumber || '',
    orderDetailsData?.orderOrigin,
    registerNumber,
    transactionId,
  );

  useEffect(() => {
    // If order number is online or H Order, call order # barcode API with order number
    if (!isCashNCarryOrderNumber && orderNumber && !isFetched) {
      // Guest Orders have no URL Params, so it must be fetched from order details
      if (!data && isGuestOrder && orderDetailsLoaded) {
        const getOrderNumberBarcodeRequest = {
          orderType: 'ONLINE',
          orderNumber,
        };
        orderBarcodeApiState.fetchData(() =>
          getSorisOrderNumberBarcode(getOrderNumberBarcodeRequest),
        );
      }
      // online orders when logged in are able to fetch with only URL params
      else if (!data && !isGuestOrder) {
        const getOrderNumberBarcodeRequest = {
          orderType: 'ONLINE',
          orderNumber,
        };
        orderBarcodeApiState.fetchData(() =>
          getSorisOrderNumberBarcode(getOrderNumberBarcodeRequest),
        );
      }
    }
  }, [orderDetailsLoaded]);

  useEffect(() => {
    const returnReceiptAllowed =
      FeatureSwitches.enableP0ReturnReceiptBarcode || !isReturnReceiptOrder;
    if (
      orderDetailsLoaded &&
      !isFetched &&
      isCashNCarryOrderNumber &&
      returnReceiptAllowed
    ) {
      const { ejRegister, ejSeqNo, ejStore, ejDate } = getEjKey(
        orderDetailsData,
        isB2CCustomer,
      );

      // Dont call barcode for in-store orders that do not yet have EJ keys available
      const areEJKeysPresent = ejRegister && ejSeqNo && ejStore && ejDate;
      if (!areEJKeysPresent) return;

      const getInStoreOrderNumberBarcodeRequest = {
        orderType: 'RECEIPT',
        ejKey: {
          ejDate,
          ejRegister,
          ejSeqNo,
          ejStore,
          transactionType,
        },
      };
      orderBarcodeApiState.fetchData(() =>
        getSorisOrderNumberBarcode(getInStoreOrderNumberBarcodeRequest),
      );
    }
  }, [orderDetailsLoaded]);

  return (
    <Phase0BarcodeContext.Provider
      value={{
        orderBarcodeApiState,
      }}
    >
      {children}
    </Phase0BarcodeContext.Provider>
  );
};
